import {
	ArrowCounterClockwiseIcon,
	CheckIcon,
	CopyIcon,
} from "@phosphor-icons/react";
import CodeEditor from "@uiw/react-textarea-code-editor";
import { type VariantProps, cva } from "class-variance-authority";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "./button";
import { cn } from "./lib/utils";

const jsonEditorVariants = cva(
	[
		"w-full min-h-120 overflow-y-auto h-full rounded-lg border transition-all duration-200 ease-in-out",
		"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
		"focus-visible:ring-ring focus-visible:ring-offset-background",
		"disabled:opacity-50 disabled:cursor-not-allowed",
		"placeholder:text-muted-foreground",
		"font-pp-neue-montreal-mono resize-none",
		"not-prose shadow-sm",
	],
	{
		variants: {
			variant: {
				default: [
					"border-input",
					"bg-background",
					"text-foreground",
					"hover:border-accent",
					"focus-visible:border-ring",
				],
				error: [
					"border-destructive",
					"bg-background",
					"text-foreground",
					"hover:border-destructive/80",
					"focus-visible:border-destructive",
					"focus-visible:ring-destructive/20",
				],
				success: [
					"border-green-500/50",
					"bg-background",
					"text-foreground",
					"hover:border-green-500/80",
					"focus-visible:border-green-500",
					"focus-visible:ring-green-500/20",
				],
			},
			size: {
				sm: "px-3 py-2 text-sm",
				md: "p-0 text-sm",
				lg: "px-4 py-3 text-base",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "md",
		},
	},
);

const messageVariants = cva("mt-1.5 text-sm", {
	variants: {
		type: {
			error: "text-destructive",
			success: "text-green-600 dark:text-green-400",
			helper: "text-muted-foreground",
		},
	},
});

const labelVariants = cva("block text-sm font-light mb-1.5", {
	variants: {
		variant: {
			default: "text-foreground",
			error: "text-foreground",
			success: "text-foreground",
		},
	},
	defaultVariants: {
		variant: "default",
	},
});

export interface JsonEditorProps
	extends Omit<
			React.TextareaHTMLAttributes<HTMLTextAreaElement>,
			"size" | "onChange"
		>,
		VariantProps<typeof jsonEditorVariants> {
	value: string;
	onChange: (value: string) => void;
	label?: string;
	helperText?: string;
	errorMessage?: string;
	successMessage?: string;
	containerClassName?: string;
	showToolbar?: boolean;
	height?: string;
}

export const JsonEditor: React.FC<JsonEditorProps> = ({
	value,
	onChange,
	variant = "default",
	size = "md",
	label,
	helperText,
	errorMessage,
	successMessage,
	className = "",
	containerClassName = "",
	showToolbar = true,
	placeholder = "Enter JSON data...",
	rows = 6,
	disabled = false,
	readOnly = false,
	height,
}) => {
	const [isValid, setIsValid] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [isFormatted, setIsFormatted] = useState(false);

	const [copied, setCopied] = useState(false);

	const validateJson = useCallback((jsonString: string) => {
		if (!jsonString.trim()) {
			setIsValid(true);
			setError(null);
			return;
		}

		try {
			JSON.parse(jsonString);
			setIsValid(true);
			setError(null);
		} catch (err) {
			setIsValid(false);
			setError(err instanceof Error ? err.message : "Invalid JSON");
		}
	}, []);

	const formatJson = useCallback(() => {
		if (!value.trim()) return;

		try {
			const parsed = JSON.parse(value);
			const formatted = JSON.stringify(parsed, null, 2);
			onChange(formatted);
			setIsFormatted(true);
			setTimeout(() => setIsFormatted(false), 2000);
		} catch {
			// Silently ignore formatting errors - validation is handled elsewhere
		}
	}, [value, onChange]);

	const copyToClipboard = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(value);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error("Failed to copy:", err);
		}
	}, [value]);

	const resetJson = useCallback(() => {
		onChange("");
	}, [onChange]);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			const newValue = e.target.value;
			onChange(newValue);
			validateJson(newValue);
		},
		[onChange, validateJson],
	);

	useEffect(() => {
		validateJson(value);
	}, [value, validateJson]);

	const effectiveVariant = !isValid
		? "error"
		: isValid && value.trim()
			? "success"
			: variant;
	const displayErrorMessage =
		(variant === "error" && errorMessage) || (!isValid && error);
	const displaySuccessMessage = variant === "success" && successMessage;

	return (
		<div className={cn("not-prose", containerClassName)}>
			{label && (
				<div className={labelVariants({ variant: effectiveVariant })}>
					{label}
				</div>
			)}

			{showToolbar && (
				<div className="flex items-center justify-between mb-2">
					<div className="flex items-center gap-2">
						<span className="text-xs font-light text-muted-foreground">
							JSON Editor
						</span>
						{!isValid && (
							<span className="text-xs text-destructive">Invalid</span>
						)}
						{isValid && value.trim() && (
							<span className="text-xs text-green-600 dark:text-green-400">
								Valid
							</span>
						)}
					</div>
					<div className="flex items-center gap-1">
						<Button
							type="button"
							variant="icon"
							size="sm"
							onClick={formatJson}
							disabled={!isValid || readOnly || !value.trim()}
							className={cn(
								"transition-opacity duration-200",
								!value.trim() && "opacity-30 pointer-events-none",
							)}
						>
							{isFormatted ? (
								<span className="text-green-600 dark:text-green-400">
									<CheckIcon size={16} />
								</span>
							) : (
								"{ }"
							)}
						</Button>
						<Button
							type="button"
							variant="icon"
							size="sm"
							onClick={copyToClipboard}
							disabled={!value.trim()}
							className={cn(
								"transition-opacity duration-200",
								!value.trim() && "opacity-30 pointer-events-none",
							)}
						>
							{copied ? (
								<span className="text-green-600 dark:text-green-400">
									<CheckIcon size={16} />
								</span>
							) : (
								<CopyIcon size={16} />
							)}
						</Button>
						<Button
							type="button"
							variant="icon"
							size="sm"
							onClick={resetJson}
							disabled={readOnly || !value.trim()}
							className={cn(
								"transition-opacity duration-200",
								!value.trim() && "opacity-30 pointer-events-none",
							)}
						>
							<ArrowCounterClockwiseIcon size={16} />
						</Button>
					</div>
				</div>
			)}

			<div
				className={cn(
					jsonEditorVariants({ variant: effectiveVariant, size }),
					className,
				)}
				style={{
					maxHeight: height ?? `${(rows || 6) * 1.5}em`,
					height: height ?? undefined,
				}}
			>
				<CodeEditor
					value={value}
					onChange={handleChange}
					language="json"
					placeholder={placeholder}
					disabled={disabled}
					readOnly={readOnly}
					data-color-mode={
						typeof window !== "undefined" &&
						window.matchMedia("(prefers-color-scheme: dark)").matches
							? "dark"
							: "light"
					}
					style={{
						fontSize: size === "sm" ? "14px" : size === "lg" ? "16px" : "14px",
						fontFamily:
							'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
						lineHeight: "1.5",
						minHeight: `${(rows || 6) * 8}em`,
						border: "none",
						outline: "none",
						padding: "0",
						margin: "0",
						background: "transparent",
						width: "100%",
						height: "100%",
						resize: "none",
					}}
				/>
			</div>

			{displayErrorMessage && (
				<p className={messageVariants({ type: "error" })}>
					{errorMessage || error}
				</p>
			)}

			{!displayErrorMessage && displaySuccessMessage && (
				<p className={messageVariants({ type: "success" })}>{successMessage}</p>
			)}

			{helperText && !displayErrorMessage && !displaySuccessMessage && (
				<p className={messageVariants({ type: "helper" })}>{helperText}</p>
			)}
		</div>
	);
};
